((global) => {
  const wrapIndex = (index, count) => {
    if (!Number.isFinite(count) || count <= 0) return 0;
    return ((index % count) + count) % count;
  };

  const physicalIndexForLogical = (index, count) => {
    if (!Number.isFinite(count) || count <= 0) return 0;
    const logicalIndex = wrapIndex(index, count);
    return count > 1 ? logicalIndex + 1 : logicalIndex;
  };

  const productUrlWithVariant = (productUrl, variantId, baseUrl = 'https://shopify.invalid') => {
    if (!productUrl) return '';
    try {
      const url = new URL(productUrl, baseUrl);
      if (variantId) url.searchParams.set('variant', String(variantId));
      else url.searchParams.delete('variant');
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return productUrl;
    }
  };

  const productLinkForSlide = (productUrl, slide) => {
    if (!slide) return productUrlWithVariant(productUrl, null);
    return slide.dataset?.variantUrl || productUrlWithVariant(productUrl, slide.dataset?.variantId);
  };

  const familyProductUrl = ({
    canonicalUrl,
    canonicalHandle,
    currentHandle,
    variantId = null,
    baseUrl = 'https://shopify.invalid',
  }) => {
    if (!canonicalUrl) return '';
    try {
      const url = new URL(canonicalUrl, baseUrl);
      url.searchParams.delete('style');
      url.searchParams.delete('variant');
      url.searchParams.delete('section_id');
      url.searchParams.delete('option_values');
      if (currentHandle && currentHandle !== canonicalHandle) url.searchParams.set('style', currentHandle);
      if (variantId) url.searchParams.set('variant', String(variantId));
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return canonicalUrl;
    }
  };

  const mediaHandoffUrl = ({
    productUrl,
    mediaId,
    mapping = null,
    baseUrl = 'https://shopify.invalid',
  }) => {
    if (!productUrl || !mediaId) return '';
    try {
      const url = new URL(productUrl, baseUrl);
      ['style', 'variant', 'section_id', 'option_values', 'sg_media', 'sg_handoff', 'sg_color', 'sg_options'].forEach((key) =>
        url.searchParams.delete(key),
      );
      url.searchParams.set('sg_media', String(mediaId));
      if (mapping?.variant?.id && mapping.colorValue && mapping.ambiguous !== true) {
        url.searchParams.set('sg_handoff', 'color');
        url.searchParams.set('sg_color', mapping.colorValue);
        url.searchParams.set('variant', String(mapping.variant.id));
      } else {
        url.searchParams.set('sg_handoff', 'media');
      }
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return productUrl;
    }
  };

  const productOptionDefinitions = (productData) =>
    Array.from(productData?.options || []).map((option, index) => ({
      name: typeof option === 'string' ? option : option?.name,
      position: Number.parseInt(typeof option === 'string' ? index + 1 : option?.position || index + 1, 10),
    }));

  const productColorOptionPosition = (productData) => {
    const definition = productOptionDefinitions(productData).find((option) => {
      const name = String(option.name || '').trim().toLocaleLowerCase();
      return name === 'color' || name === 'colour';
    });
    return definition?.position || 0;
  };

  const variantOptionValue = (variant, position) => {
    if (!position) return '';
    if (Array.isArray(variant?.options)) return variant.options[position - 1] || '';
    return variant?.[`option${position}`] || '';
  };

  const mediaIdentifier = (media) => media?.id || media?.preview_image?.id || null;

  const variantMediaIdentifier = (variant, source) => {
    const media = source === 'featured_media' ? variant?.featured_media : variant?.featured_image;
    return media?.id || null;
  };

  const mappedVariantForMedia = ({ media, variants, colorOptionPosition = 0 }) => {
    const mediaIds = [media?.id, media?.preview_image?.id].filter(Boolean).map(String);
    if (!mediaIds.length || !Array.isArray(variants) || variants.length <= 1) return null;
    let mappingPriority = 3;
    let mappingSource = '';
    let mappedColorValue = '';
    let ambiguous = false;
    let firstMappedVariant = null;
    let firstAvailableMappedVariant = null;

    variants.forEach((variant) => {
      let candidatePriority = 0;
      let candidateSource = '';
      if (mediaIds.includes(String(variantMediaIdentifier(variant, 'featured_media')))) {
        candidatePriority = 1;
        candidateSource = 'featured_media';
      } else if (mediaIds.includes(String(variantMediaIdentifier(variant, 'featured_image')))) {
        candidatePriority = 2;
        candidateSource = 'featured_image';
      }
      if (!candidatePriority) return;

      const candidateColorValue = variantOptionValue(variant, colorOptionPosition);
      if (candidatePriority < mappingPriority) {
        mappingPriority = candidatePriority;
        mappingSource = candidateSource;
        mappedColorValue = candidateColorValue;
        ambiguous = false;
        firstMappedVariant = variant;
        firstAvailableMappedVariant = variant.available === true ? variant : null;
        return;
      }
      if (candidatePriority === mappingPriority) {
        const matchingColor = !colorOptionPosition || candidateColorValue === mappedColorValue;
        if (!matchingColor) ambiguous = true;
        if (matchingColor && variant.available === true && !firstAvailableMappedVariant) {
          firstAvailableMappedVariant = variant;
        }
      }
    });

    const variant = firstAvailableMappedVariant || firstMappedVariant;
    if (!variant) return null;
    return { ambiguous, variant, source: mappingSource, colorValue: mappedColorValue };
  };

  const productMediaContext = ({
    productData,
    canonicalUrl,
    canonicalHandle,
    currentHandle,
    productUrl = '',
    baseUrl = 'https://shopify.invalid',
  }) => {
    const variants = Array.from(productData?.variants || []);
    const defaultVariant = variants.find((variant) => variant.available === true) || variants[0] || null;
    const colorOptionPosition = productColorOptionPosition(productData);
    const cleanProductUrl = productUrl
      ? productUrlWithVariant(productUrl, null, baseUrl)
      : familyProductUrl({
          canonicalUrl,
          canonicalHandle,
          currentHandle,
          baseUrl,
        });
    const media = Array.from(productData?.media || [])
      .map((item, index) => {
        const preview = item?.preview_image || item;
        const source = preview?.src || item?.src;
        const id = mediaIdentifier(item);
        if (!id || !source) return null;
        const mapping = mappedVariantForMedia({ media: item, variants, colorOptionPosition });
        return {
          id,
          position: Number(item.position) || index + 1,
          mediaType: item.media_type || 'image',
          alt: item.alt || productData?.title || '',
          preview: {
            id: preview?.id || null,
            src: source,
            width: Number(preview.width) || 1,
            height: Number(preview.height) || 1,
          },
          variantId: mapping?.variant?.id || null,
          variantUrl: productUrl
            ? mediaHandoffUrl({ productUrl, mediaId: id, mapping, baseUrl })
            : mapping
              ? familyProductUrl({
                  canonicalUrl,
                  canonicalHandle,
                  currentHandle,
                  variantId: mapping.variant.id,
                  baseUrl,
                })
              : '',
          mappingSource: mapping?.source || '',
          colorValue: mapping?.colorValue || '',
        };
      })
      .filter(Boolean);

    const preferredMediaIds = [
      variantMediaIdentifier(defaultVariant, 'featured_media'),
      variantMediaIdentifier(defaultVariant, 'featured_image'),
      productData?.featured_media?.id,
      typeof productData?.featured_image === 'object' ? productData.featured_image?.id : null,
    ].filter(Boolean);
    let activeIndex = preferredMediaIds
      .map((id) =>
        media.findIndex(
          (item) => String(item.id) === String(id) || String(item.preview.id) === String(id),
        ),
      )
      .find((index) => index >= 0);
    if (!Number.isInteger(activeIndex)) activeIndex = 0;

    return {
      productId: productData?.id || null,
      productTitle: productData?.title || '',
      cleanProductUrl,
      defaultVariantId: defaultVariant?.id || null,
      activeIndex,
      colorOptionPosition,
      media,
    };
  };

  const loopNavigationPlan = ({ previousIndex, nextIndex, direction = 0, count, animate = true }) => {
    const physicalIndex = physicalIndexForLogical(nextIndex, count);
    if (!animate || count <= 1) return { physicalIndex, correctionPhysicalIndex: null };
    if (direction > 0 && previousIndex === count - 1 && nextIndex === 0) {
      return { physicalIndex: count + 1, correctionPhysicalIndex: 1 };
    }
    if (direction < 0 && previousIndex === 0 && nextIndex === count - 1) {
      return { physicalIndex: 0, correctionPhysicalIndex: count };
    }
    return { physicalIndex, correctionPhysicalIndex: null };
  };

  const carouselGestureProfiles = Object.freeze({
    touch: Object.freeze({
      deadZone: 5,
      horizontalRatio: 1.04,
      verticalRatio: 1.2,
      maximumUndecidedDistance: 36,
      maximumUndecidedSamples: 3,
      distanceRatio: 0.065,
      minimumDistance: 22,
      maximumDistance: 36,
      velocityThreshold: 0.2,
      velocityDistance: 14,
    }),
    mouse: Object.freeze({
      deadZone: 8,
      horizontalRatio: 1.15,
      verticalRatio: 1,
      maximumUndecidedDistance: Number.POSITIVE_INFINITY,
      maximumUndecidedSamples: Number.POSITIVE_INFINITY,
      distanceRatio: 0.1,
      minimumDistance: 32,
      maximumDistance: 72,
      velocityThreshold: 0.45,
      velocityDistance: 18,
    }),
  });
  const responsiveTouchGestureProfile = Object.freeze({
    deadZone: 5,
    horizontalRatio: 1.02,
    verticalRatio: 1.35,
    maximumUndecidedDistance: 16,
    maximumUndecidedSamples: 2,
    distanceRatio: 0.035,
    minimumDistance: 12,
    maximumDistance: 22,
    velocityThreshold: 0.13,
    velocityDistance: 8,
  });
  const anticipatoryTouchGestureProfile = Object.freeze({
    deadZone: 3,
    probableDistance: 4,
    confirmedDistance: 6,
    horizontalRatio: 1.01,
    verticalRatio: 1.4,
    maximumUndecidedDistance: 12,
    maximumUndecidedSamples: 2,
    distanceRatio: 0.03,
    minimumDistance: 10,
    maximumDistance: 18,
    velocityThreshold: 0.1,
    projectionVelocityThreshold: 0.07,
    velocityDistance: 6,
    strongTrajectoryDistance: 8,
    strongTrajectoryRatio: 1,
    stoppingDeceleration: 0.004,
    predictionHorizon: 32,
    fallbackPredictionHorizon: 28,
    predictionStaleAge: 48,
    visualLeadRatio: 0.04,
    minimumVisualLead: 8,
    maximumVisualLead: 18,
    previewDamping: 0.35,
    previewLeadDamping: 0.2,
    previewClamp: 6,
    reversalDistance: 5,
    leadVelocityFloor: 0.035,
  });
  const recentVelocityWindow = 100;
  const recentVelocityMaximumAge = 140;
  const recentVelocitySampleLimit = 12;
  const recentTrajectoryWindow = 72;
  const anticipatoryVelocityWindow = 80;
  const anticipatoryVelocityMaximumAge = 120;
  const anticipatoryTrajectoryWindow = 64;
  const anticipatorySampleLimit = 16;
  const anticipatoryHistoryWindow = 180;
  const predictedSampleLimit = 4;
  const predictedInputLimit = 8;

  const carouselGestureProfile = (pointerType = 'touch') =>
    pointerType === 'mouse' ? carouselGestureProfiles.mouse : carouselGestureProfiles.touch;

  const carouselDistanceThreshold = (width, pointerType = 'touch') => {
    const profile = carouselGestureProfile(pointerType);
    const viewportWidth = Math.max(width || 0, 1);
    return Math.max(
      profile.minimumDistance,
      Math.min(viewportWidth * profile.distanceRatio, profile.maximumDistance),
    );
  };

  const responsiveTouchDistanceThreshold = (width) => {
    const viewportWidth = Math.max(width || 0, 1);
    return Math.max(
      responsiveTouchGestureProfile.minimumDistance,
      Math.min(
        viewportWidth * responsiveTouchGestureProfile.distanceRatio,
        responsiveTouchGestureProfile.maximumDistance,
      ),
    );
  };

  const anticipatoryTouchDistanceThreshold = (width) => {
    const viewportWidth = Math.max(Number(width) || 0, 1);
    return Math.max(
      anticipatoryTouchGestureProfile.minimumDistance,
      Math.min(
        viewportWidth * anticipatoryTouchGestureProfile.distanceRatio,
        anticipatoryTouchGestureProfile.maximumDistance,
      ),
    );
  };

  const recordRecentPointerSample = (samples, x, time, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(time)) return Array.isArray(samples) ? samples : [];
    const nextSamples = Array.isArray(samples) ? samples.slice() : [];
    const latest = nextSamples[nextSamples.length - 1];
    if (latest?.time === time) {
      latest.x = x;
      if (Number.isFinite(y)) latest.y = y;
      return nextSamples;
    }
    const sample = { x, time };
    if (Number.isFinite(y)) sample.y = y;
    nextSamples.push(sample);
    const cutoff = time - recentVelocityWindow;
    while (nextSamples.length > 2 && nextSamples[1].time < cutoff) nextSamples.shift();
    if (nextSamples.length > recentVelocitySampleLimit) {
      nextSamples.splice(0, nextSamples.length - recentVelocitySampleLimit);
    }
    return nextSamples;
  };

  const recentPointerVelocity = ({ samples, now }) => {
    if (!Array.isArray(samples) || samples.length < 2 || !Number.isFinite(now)) return 0;
    const newest = samples[samples.length - 1];
    if (!Number.isFinite(newest?.time) || now < newest.time || now - newest.time > recentVelocityMaximumAge) return 0;
    const cutoff = newest.time - recentVelocityWindow;
    const oldest = samples.find((sample) => sample.time >= cutoff && sample.time < newest.time);
    if (!oldest) return 0;
    const elapsed = newest.time - oldest.time;
    if (elapsed <= 0) return 0;
    return (newest.x - oldest.x) / elapsed;
  };

  const recentPointerTrajectory = ({ samples, now }) => {
    if (!Array.isArray(samples) || samples.length < 2 || !Number.isFinite(now)) return { x: 0, y: 0 };
    const newest = samples[samples.length - 1];
    if (!Number.isFinite(newest?.time) || !Number.isFinite(newest?.y) || now < newest.time) return { x: 0, y: 0 };
    const cutoff = newest.time - recentTrajectoryWindow;
    const oldest = samples.find(
      (sample) => sample.time >= cutoff && sample.time < newest.time && Number.isFinite(sample.y),
    );
    if (!oldest) return { x: 0, y: 0 };
    return { x: newest.x - oldest.x, y: newest.y - oldest.y };
  };

  const normalizePointerTimestamp = (time, now) =>
    Number.isFinite(time) && Number.isFinite(now) && Math.abs(time - now) < 60_000 ? time : now;

  const recordAnticipatoryPointerSample = (samples, x, time, y) => {
    const nextSamples = Array.isArray(samples) ? samples.slice() : [];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(time)) return nextSamples;
    const latest = nextSamples[nextSamples.length - 1];
    if (latest && time <= latest.time) return nextSamples;
    nextSamples.push({ x, y, time });
    const cutoff = time - anticipatoryHistoryWindow;
    while (nextSamples.length > 2 && nextSamples[1].time < cutoff) nextSamples.shift();
    if (nextSamples.length > anticipatorySampleLimit) {
      nextSamples.splice(0, nextSamples.length - anticipatorySampleLimit);
    }
    return nextSamples;
  };

  const orderPointerSamplesByTime = (samples) => {
    const orderedSamples = [];

    samples.forEach((sample) => {
      let insertionIndex = orderedSamples.length;
      while (
        insertionIndex > 0 &&
        orderedSamples[insertionIndex - 1].time > sample.time
      ) {
        insertionIndex -= 1;
      }
      orderedSamples.splice(insertionIndex, 0, sample);
    });

    return orderedSamples;
  };

  const pointerEventSampleBatch = (event, now, previousTime = Number.NEGATIVE_INFINITY) => {
    let coalesced = [];
    let predicted = [];
    let predictedSupported = false;
    if (typeof event?.getCoalescedEvents === 'function') {
      try {
        coalesced = Array.from(event.getCoalescedEvents() || []);
      } catch {
        coalesced = [];
      }
    }
    if (typeof event?.getPredictedEvents === 'function') {
      predictedSupported = true;
      try {
        predicted = Array.from(event.getPredictedEvents() || []).slice(0, predictedInputLimit);
      } catch {
        predicted = [];
      }
    }

    const actual = {
      x: Number(event?.clientX),
      y: Number(event?.clientY),
      time: normalizePointerTimestamp(Number(event?.timeStamp), now),
    };
    const candidates = orderPointerSamplesByTime(
      [...coalesced, event]
        .map((sample) => ({
          x: Number(sample?.clientX),
          y: Number(sample?.clientY),
          time: normalizePointerTimestamp(Number(sample && sample.timeStamp), now),
        }))
        .filter(
          (sample) =>
            Number.isFinite(sample.x) &&
            Number.isFinite(sample.y) &&
            Number.isFinite(sample.time),
        ),
    );
    const realSamples = [];
    let lastTime = previousTime;
    candidates.forEach((sample) => {
      if (sample.time <= lastTime) return;
      realSamples.push(sample);
      lastTime = sample.time;
    });

    const predictedSamples = orderPointerSamplesByTime(
      predicted
        .map((sample) => ({
          x: Number(sample?.clientX),
          y: Number(sample?.clientY),
          time: normalizePointerTimestamp(Number(sample && sample.timeStamp), now),
        }))
        .filter(
          (sample) =>
            Number.isFinite(sample.x) &&
            Number.isFinite(sample.y) &&
            Number.isFinite(sample.time),
        ),
    ).slice(0, predictedSampleLimit);

    return { actual, predictedSamples, predictedSupported, realSamples };
  };

  const anticipatoryPointerVelocity = ({ samples, now }) => {
    if (!Array.isArray(samples) || samples.length < 2 || !Number.isFinite(now)) return 0;
    const newest = samples[samples.length - 1];
    if (
      !Number.isFinite(newest?.time) ||
      now < newest.time ||
      now - newest.time > anticipatoryVelocityMaximumAge
    ) {
      return 0;
    }
    const cutoff = newest.time - anticipatoryVelocityWindow;
    const oldest = samples.find((sample) => sample.time >= cutoff && sample.time < newest.time);
    if (!oldest) return 0;
    const elapsed = newest.time - oldest.time;
    return elapsed > 0 ? (newest.x - oldest.x) / elapsed : 0;
  };

  const anticipatoryPointerTrajectory = ({ samples, now }) => {
    if (!Array.isArray(samples) || samples.length < 2 || !Number.isFinite(now)) {
      return { x: 0, y: 0, consistency: 0 };
    }
    const newest = samples[samples.length - 1];
    if (
      !Number.isFinite(newest?.time) ||
      now < newest.time ||
      now - newest.time > anticipatoryVelocityMaximumAge
    ) {
      return { x: 0, y: 0, consistency: 0 };
    }
    const cutoff = newest.time - anticipatoryTrajectoryWindow;
    const recentSamples = samples.filter((sample) => sample.time >= cutoff && sample.time <= newest.time);
    if (recentSamples.length < 2) return { x: 0, y: 0, consistency: 0 };
    const oldest = recentSamples[0];
    let horizontalPath = 0;
    for (let index = 1; index < recentSamples.length; index += 1) {
      horizontalPath += Math.abs(recentSamples[index].x - recentSamples[index - 1].x);
    }
    const x = newest.x - oldest.x;
    return {
      x,
      y: newest.y - oldest.y,
      consistency: horizontalPath > 0 ? Math.min(Math.abs(x) / horizontalPath, 1) : 0,
    };
  };

  const fullPointerTrajectory = (samples) => {
    if (!Array.isArray(samples) || samples.length < 2) return { x: 0, y: 0, consistency: 0 };
    const first = samples[0];
    const final = samples[samples.length - 1];
    let horizontalPath = 0;
    for (let index = 1; index < samples.length; index += 1) {
      horizontalPath += Math.abs(samples[index].x - samples[index - 1].x);
    }
    const x = final.x - first.x;
    return {
      x,
      y: final.y - first.y,
      consistency: horizontalPath > 0 ? Math.min(Math.abs(x) / horizontalPath, 1) : 0,
    };
  };

  const recentVerticalAcceleration = (samples) => {
    if (!Array.isArray(samples) || samples.length < 3) return 0;
    const final = samples[samples.length - 1];
    const middle = samples[samples.length - 2];
    const first = samples[samples.length - 3];
    const recentElapsed = final.time - middle.time;
    const previousElapsed = middle.time - first.time;
    if (recentElapsed <= 0 || previousElapsed <= 0) return 0;
    const recentVelocity = Math.abs((final.y - middle.y) / recentElapsed);
    const previousVelocity = Math.abs((middle.y - first.y) / previousElapsed);
    return recentVelocity - previousVelocity;
  };

  const anticipatoryGestureIntent = ({
    deltaX,
    deltaY,
    recentDeltaX = 0,
    recentDeltaY = 0,
    meaningfulSamples = 0,
    verticalAcceleration = 0,
  }) => {
    const absoluteX = Math.abs(Number(deltaX) || 0);
    const absoluteY = Math.abs(Number(deltaY) || 0);
    const recentAbsoluteX = Math.abs(Number(recentDeltaX) || 0);
    const recentAbsoluteY = Math.abs(Number(recentDeltaY) || 0);
    const distance = Math.hypot(absoluteX, absoluteY);
    if (distance <= anticipatoryTouchGestureProfile.deadZone) return 'undecided';

    const strongVertical =
      (absoluteY >= anticipatoryTouchGestureProfile.probableDistance &&
        absoluteY >= absoluteX * anticipatoryTouchGestureProfile.verticalRatio) ||
      (recentAbsoluteY >= anticipatoryTouchGestureProfile.probableDistance &&
        recentAbsoluteY >= recentAbsoluteX * anticipatoryTouchGestureProfile.verticalRatio) ||
      (verticalAcceleration >= 0.08 && recentAbsoluteY > recentAbsoluteX * 1.1);
    if (strongVertical) return 'vertical';

    const horizontalEvidence =
      absoluteX >= absoluteY * anticipatoryTouchGestureProfile.horizontalRatio ||
      recentAbsoluteX > recentAbsoluteY;
    if (absoluteX >= anticipatoryTouchGestureProfile.confirmedDistance && horizontalEvidence) {
      return 'horizontal';
    }

    if (
      distance >= anticipatoryTouchGestureProfile.maximumUndecidedDistance ||
      meaningfulSamples >= anticipatoryTouchGestureProfile.maximumUndecidedSamples
    ) {
      if (recentAbsoluteX > recentAbsoluteY && recentAbsoluteX >= anticipatoryTouchGestureProfile.probableDistance) {
        return 'horizontal';
      }
      return absoluteX > absoluteY ? 'horizontal' : 'vertical';
    }

    if (
      absoluteX >= anticipatoryTouchGestureProfile.probableDistance &&
      (absoluteX >= absoluteY || recentAbsoluteX > recentAbsoluteY)
    ) {
      return 'probable-horizontal';
    }
    return 'undecided';
  };

  const anticipatoryDirection = ({ currentDirection = 0, deltaX = 0, recentDeltaX = 0 }) => {
    const candidateDelta =
      Math.abs(recentDeltaX) >= anticipatoryTouchGestureProfile.reversalDistance ? recentDeltaX : deltaX;
    const candidate = Math.sign(candidateDelta);
    if (!candidate) return currentDirection;
    if (!currentDirection || candidate === currentDirection) return candidate;
    return Math.abs(recentDeltaX) >= anticipatoryTouchGestureProfile.reversalDistance
      ? candidate
      : currentDirection;
  };

  const anticipatoryVisualLeadLimit = (width) =>
    Math.max(
      anticipatoryTouchGestureProfile.minimumVisualLead,
      Math.min(
        Math.max(Number(width) || 0, 1) * anticipatoryTouchGestureProfile.visualLeadRatio,
        anticipatoryTouchGestureProfile.maximumVisualLead,
      ),
    );

  const anticipatoryVisualLead = ({
    samples,
    predictedSamples = [],
    predictedSupported = false,
    actualX,
    actualTime,
    now,
    width,
    direction = 0,
  }) => {
    const velocityX = anticipatoryPointerVelocity({ samples, now });
    if (
      !Number.isFinite(actualX) ||
      !Number.isFinite(actualTime) ||
      !Number.isFinite(now) ||
      now - actualTime > anticipatoryTouchGestureProfile.predictionStaleAge ||
      Math.abs(velocityX) < anticipatoryTouchGestureProfile.leadVelocityFloor
    ) {
      return 0;
    }
    const validPredictions = predictedSamples.filter(
      (sample) =>
        sample.time > actualTime &&
        sample.time - actualTime <= anticipatoryTouchGestureProfile.predictionHorizon,
    );
    let lead;
    if (validPredictions.length > 0) {
      lead = validPredictions[validPredictions.length - 1].x - actualX;
      if (Math.sign(lead) !== Math.sign(velocityX)) return 0;
    } else {
      lead = velocityX * anticipatoryTouchGestureProfile.fallbackPredictionHorizon;
      if (predictedSupported && predictedSamples.length > 0) return 0;
    }
    if (direction && Math.sign(lead) !== direction) return 0;
    const limit = anticipatoryVisualLeadLimit(width);
    if (Math.abs(lead) > limit * 3) return 0;
    return Math.max(-limit, Math.min(limit, lead));
  };

  const anticipatoryReleaseDecision = ({
    deltaX,
    width,
    velocityX = 0,
    samples = [],
    now,
    horizontalIntent = false,
  }) => {
    const threshold = anticipatoryTouchDistanceThreshold(width);
    if (!horizontalIntent) {
      return {
        accepted: false,
        direction: 0,
        distanceThreshold: threshold,
        projectedStoppingDistance: 0,
        reason: 'intent',
      };
    }
    const trajectory = anticipatoryPointerTrajectory({ samples, now });
    const overallTrajectory = fullPointerTrajectory(samples);
    const totalDirection = Math.sign(deltaX);
    const recentDirection = Math.sign(trajectory.x);
    const reversed =
      recentDirection &&
      totalDirection &&
      recentDirection !== totalDirection &&
      Math.abs(trajectory.x) >= anticipatoryTouchGestureProfile.reversalDistance &&
      trajectory.consistency >= 0.7;
    const finalDirection = reversed ? recentDirection : totalDirection || recentDirection;
    const matchingVelocity =
      finalDirection &&
      Math.sign(velocityX) === finalDirection &&
      Math.abs(velocityX) >= anticipatoryTouchGestureProfile.velocityThreshold;
    const matchingProjectionVelocity =
      finalDirection &&
      Math.sign(velocityX) === finalDirection &&
      Math.abs(velocityX) >= anticipatoryTouchGestureProfile.projectionVelocityThreshold;
    const actualMovement = reversed
      ? Math.abs(trajectory.x)
      : Math.max(Math.abs(deltaX), finalDirection === recentDirection ? Math.abs(trajectory.x) : 0);
    const projectedStoppingDistance =
      actualMovement +
      (matchingProjectionVelocity
        ? (velocityX * velocityX) / (2 * anticipatoryTouchGestureProfile.stoppingDeceleration)
        : 0);
    const distanceCommit = actualMovement >= threshold;
    const velocityCommit =
      matchingVelocity && actualMovement >= anticipatoryTouchGestureProfile.velocityDistance;
    const projectedCommit =
      matchingProjectionVelocity &&
      actualMovement >= anticipatoryTouchGestureProfile.velocityDistance &&
      projectedStoppingDistance >= threshold;
    const strongTrajectoryCommit =
      actualMovement >= anticipatoryTouchGestureProfile.strongTrajectoryDistance &&
      Math.sign(overallTrajectory.x) === finalDirection &&
      Math.abs(overallTrajectory.x) >=
        Math.abs(overallTrajectory.y) * anticipatoryTouchGestureProfile.strongTrajectoryRatio &&
      overallTrajectory.consistency >= 0.72;
    const accepted = Boolean(
      finalDirection && (distanceCommit || velocityCommit || projectedCommit || strongTrajectoryCommit),
    );
    let reason = 'rejected';
    if (distanceCommit) reason = 'distance';
    else if (velocityCommit) reason = 'velocity';
    else if (projectedCommit) reason = 'stopping-distance';
    else if (strongTrajectoryCommit) reason = 'trajectory';
    return {
      accepted,
      direction: accepted ? (finalDirection < 0 ? 1 : -1) : 0,
      distanceThreshold: threshold,
      actualMovement,
      projectedStoppingDistance,
      reversed,
      reason,
    };
  };

  const anticipatoryReleaseDuration = ({
    width,
    deltaX,
    velocityX = 0,
    accepted = false,
    distanceThreshold,
  }) => {
    const viewportWidth = Math.max(Number(width) || 0, 1);
    const threshold = Math.max(Number(distanceThreshold) || anticipatoryTouchDistanceThreshold(width), 1);
    const distance = Math.abs(Number(deltaX) || 0);
    if (!accepted && distance <= anticipatoryTouchGestureProfile.deadZone) return 0;
    const progress = Math.min(distance / viewportWidth, 1);
    const speedRatio = Math.min(Math.abs(Number(velocityX) || 0) / 0.8, 1);
    if (accepted) {
      return Math.round(Math.max(90, Math.min(180, 175 - progress * 45 - speedRatio * 75)));
    }
    const returnProgress = Math.min(distance / threshold, 1);
    return Math.round(Math.max(70, Math.min(130, 95 + returnProgress * 35 - speedRatio * 25)));
  };

  const swipeDirection = ({
    deltaX,
    deltaY,
    duration,
    width,
    zoomed = false,
    pointerType = 'touch',
    velocityX,
    horizontalIntent = false,
  }) => {
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);
    const elapsed = Math.max(duration, 1);
    const viewportWidth = Math.max(width || 0, 1);

    if (zoomed) {
      const strongDistance = Math.max(96, Math.min(viewportWidth * 0.28, 180));
      if (absoluteX <= absoluteY * 1.35 || absoluteX < strongDistance || absoluteX / elapsed < 0.55) return 0;
      return deltaX < 0 ? 1 : -1;
    }

    const profile = carouselGestureProfile(pointerType);
    const distanceThreshold = carouselDistanceThreshold(viewportWidth, pointerType);
    const measuredVelocity = Number.isFinite(velocityX) ? velocityX : deltaX / elapsed;
    const matchingVelocity =
      Math.sign(measuredVelocity) === Math.sign(deltaX) &&
      Math.abs(measuredVelocity) >= profile.velocityThreshold &&
      absoluteX >= profile.velocityDistance;

    if (!horizontalIntent && absoluteX <= absoluteY * profile.horizontalRatio) return 0;
    if (absoluteX < distanceThreshold && !matchingVelocity) return 0;
    return deltaX < 0 ? 1 : -1;
  };

  const responsiveSwipeDirection = (options) => {
    if (options.pointerType === 'mouse') return swipeDirection(options);
    const absoluteX = Math.abs(options.deltaX);
    const absoluteY = Math.abs(options.deltaY);
    const elapsed = Math.max(options.duration, 1);
    const measuredVelocity = Number.isFinite(options.velocityX)
      ? options.velocityX
      : options.deltaX / elapsed;
    const matchingVelocity =
      Math.sign(measuredVelocity) === Math.sign(options.deltaX) &&
      Math.abs(measuredVelocity) >= responsiveTouchGestureProfile.velocityThreshold &&
      absoluteX >= responsiveTouchGestureProfile.velocityDistance;
    if (!options.horizontalIntent && absoluteX <= absoluteY * responsiveTouchGestureProfile.horizontalRatio) return 0;
    if (absoluteX < responsiveTouchDistanceThreshold(options.width) && !matchingVelocity) return 0;
    return options.deltaX < 0 ? 1 : -1;
  };

  const zoomGestureOutcome = ({ deltaX, deltaY, duration, width, height }) => {
    const distance = Math.hypot(deltaX, deltaY);
    if (distance <= 6) return { type: 'click', direction: 0 };

    const direction = swipeDirection({ deltaX, deltaY, duration, width, zoomed: true });
    if (direction) return { type: 'navigate', direction };

    const elapsed = Math.max(duration, 1);
    const shortestViewportEdge = Math.max(1, Math.min(width || 0, height || 0));
    const swipeDistance = Math.max(72, Math.min(shortestViewportEdge * 0.18, 120));
    if (distance >= swipeDistance && distance / elapsed >= 0.5) return { type: 'swipe', direction: 0 };

    return { type: 'drag', direction: 0 };
  };

  const gestureIntent = ({
    deltaX,
    deltaY,
    threshold = 8,
    ratio = 1.15,
    verticalRatio = 1,
    maximumUndecidedDistance = Number.POSITIVE_INFINITY,
    undecidedSamples = 0,
    maximumUndecidedSamples = Number.POSITIVE_INFINITY,
    recentDeltaX = 0,
    recentDeltaY = 0,
  }) => {
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);
    const distance = Math.hypot(deltaX, deltaY);
    if (distance <= threshold) return 0;
    if (absoluteX >= absoluteY * ratio) return 1;
    if (absoluteY >= absoluteX * verticalRatio) return -1;
    const recentAbsoluteX = Math.abs(recentDeltaX);
    const recentAbsoluteY = Math.abs(recentDeltaY);
    if (Math.hypot(recentDeltaX, recentDeltaY) > threshold) {
      if (recentAbsoluteX >= recentAbsoluteY * ratio) return 1;
      if (recentAbsoluteY >= recentAbsoluteX * verticalRatio) return -1;
    }
    if (distance >= maximumUndecidedDistance || undecidedSamples >= maximumUndecidedSamples) {
      return absoluteX >= absoluteY ? 1 : -1;
    }
    return 0;
  };

  const carouselReleaseDuration = ({ width, deltaX, velocityX = 0, accepted = false }) => {
    const viewportWidth = Math.max(Number(width) || 0, 1);
    const draggedDistance = Math.min(Math.abs(Number(deltaX) || 0), viewportWidth);
    const progress = draggedDistance / viewportWidth;
    const speed = Math.min(Math.abs(Number(velocityX) || 0), 1.2);
    if (accepted) {
      const remainingRatio = 1 - progress;
      return Math.round(Math.max(140, Math.min(220, 130 + remainingRatio * 90 - speed * 35)));
    }
    return Math.round(Math.max(100, Math.min(170, 90 + progress * 110 - speed * 30)));
  };

  const clampScale = (value, minimum = 1, maximum = 5) => {
    const safeMinimum = Number.isFinite(minimum) ? minimum : 1;
    const safeMaximum = Number.isFinite(maximum) ? Math.max(maximum, safeMinimum) : Math.max(5, safeMinimum);
    const safeValue = Number.isFinite(value) ? value : safeMinimum;
    return Math.min(safeMaximum, Math.max(safeMinimum, safeValue));
  };

  const normalizeWheelDelta = ({
    deltaY,
    deltaMode = 0,
    lineHeight = 16,
    pageHeight = 800,
    maximumMagnitude = 240,
  }) => {
    let normalized = Number(deltaY) || 0;
    if (deltaMode === 1) normalized *= Math.max(Number(lineHeight) || 0, 1);
    else if (deltaMode === 2) normalized *= Math.max(Number(pageHeight) || 0, 1);
    const limit = Math.max(Number(maximumMagnitude) || 0, 1);
    return Math.max(-limit, Math.min(limit, normalized));
  };

  const wheelScaleFromDelta = ({
    scale,
    deltaY,
    minimumScale = 1,
    maximumScale = 5,
    sensitivity = 0.002,
  }) =>
    clampScale(
      (Number(scale) || minimumScale) * Math.exp(-(Number(deltaY) || 0) * sensitivity),
      minimumScale,
      maximumScale,
    );

  const zoomTransformAroundPoint = ({
    point,
    center,
    scale,
    nextScale,
    panX = 0,
    panY = 0,
  }) => {
    const safeScale = Math.max(Number(scale) || 0, 0.0001);
    const safeNextScale = Math.max(Number(nextScale) || 0, 0.0001);
    const centerX = Number(center?.x) || 0;
    const centerY = Number(center?.y) || 0;
    const pointX = Number(point?.x) || centerX;
    const pointY = Number(point?.y) || centerY;
    const imageX = (pointX - centerX - (Number(panX) || 0)) / safeScale;
    const imageY = (pointY - centerY - (Number(panY) || 0)) / safeScale;
    return {
      scale: safeNextScale,
      x: pointX - centerX - imageX * safeNextScale,
      y: pointY - centerY - imageY * safeNextScale,
    };
  };

  const containedImageBounds = ({
    bounds,
    naturalWidth,
    naturalHeight,
    objectFit = 'contain',
    objectPosition = '50% 50%',
  }) => {
    if (!bounds || ![bounds.left, bounds.top, bounds.width, bounds.height].every(Number.isFinite)) return null;
    const boxWidth = Math.max(bounds.width, 0);
    const boxHeight = Math.max(bounds.height, 0);
    const intrinsicWidth = Math.max(Number(naturalWidth) || 0, 0);
    const intrinsicHeight = Math.max(Number(naturalHeight) || 0, 0);
    const fit = String(objectFit || '').trim().toLowerCase();
    if (!boxWidth || !boxHeight || !intrinsicWidth || !intrinsicHeight || !['contain', 'scale-down'].includes(fit)) {
      return { left: bounds.left, top: bounds.top, width: boxWidth, height: boxHeight };
    }

    let scale = Math.min(boxWidth / intrinsicWidth, boxHeight / intrinsicHeight);
    if (fit === 'scale-down') scale = Math.min(scale, 1);
    const width = intrinsicWidth * scale;
    const height = intrinsicHeight * scale;
    const freeX = boxWidth - width;
    const freeY = boxHeight - height;
    const tokens = String(objectPosition || '50% 50%').trim().split(/\s+/).filter(Boolean);
    let horizontal = tokens[0] || '50%';
    let vertical = tokens[1] || '50%';
    if (tokens.length === 1) {
      if (['top', 'bottom'].includes(horizontal.toLowerCase())) {
        vertical = horizontal;
        horizontal = '50%';
      } else {
        vertical = '50%';
      }
    } else if (
      ['top', 'bottom'].includes(horizontal.toLowerCase()) &&
      ['left', 'right', 'center'].includes(vertical.toLowerCase())
    ) {
      [horizontal, vertical] = [vertical, horizontal];
    }
    const positionOffset = (token, freeSpace, startKeyword, endKeyword) => {
      const normalized = String(token || '').trim().toLowerCase();
      if (normalized === startKeyword) return 0;
      if (normalized === endKeyword) return freeSpace;
      if (normalized === 'center') return freeSpace / 2;
      if (normalized.endsWith('%')) {
        const percentage = Number.parseFloat(normalized);
        if (Number.isFinite(percentage)) return freeSpace * percentage / 100;
      }
      if (normalized.endsWith('px')) {
        const pixels = Number.parseFloat(normalized);
        if (Number.isFinite(pixels)) return pixels;
      }
      return freeSpace / 2;
    };
    return {
      left: bounds.left + positionOffset(horizontal, freeX, 'left', 'right'),
      top: bounds.top + positionOffset(vertical, freeY, 'top', 'bottom'),
      width,
      height,
    };
  };

  const desktopZoomReturnGeometry = ({
    scale,
    initialScale = 2,
    sourceBounds,
    viewportWidth,
    viewportHeight,
    viewportLeft = 0,
    viewportTop = 0,
    userPanX = 0,
    userPanY = 0,
  }) => {
    if (!sourceBounds) return null;
    const safeInitialScale = Math.max(Number(initialScale) || 1, 1);
    const safeScale = Math.max(Number(scale) || 1, 1);
    const normalizedScale =
      safeInitialScale <= 1 ? (safeScale > 1 ? 1 : 0) : (safeScale - 1) / (safeInitialScale - 1);
    const centerProgress = Math.max(0, Math.min(1, normalizedScale));
    const easedCenterProgress = centerProgress * centerProgress * (3 - 2 * centerProgress);
    const sourceCenterX = sourceBounds.left + sourceBounds.width / 2;
    const sourceCenterY = sourceBounds.top + sourceBounds.height / 2;
    const viewportCenterX = viewportLeft + Math.max(Number(viewportWidth) || 0, 0) / 2;
    const viewportCenterY = viewportTop + Math.max(Number(viewportHeight) || 0, 0) / 2;
    const centerX = sourceCenterX + (viewportCenterX - sourceCenterX) * easedCenterProgress;
    const centerY = sourceCenterY + (viewportCenterY - sourceCenterY) * easedCenterProgress;
    return {
      bounds: {
        left: centerX - sourceBounds.width / 2,
        top: centerY - sourceBounds.height / 2,
        width: sourceBounds.width,
        height: sourceBounds.height,
      },
      centerProgress: easedCenterProgress,
      returnProgress: 1 - easedCenterProgress,
      panDamping: easedCenterProgress,
      panX: (Number(userPanX) || 0) * easedCenterProgress,
      panY: (Number(userPanY) || 0) * easedCenterProgress,
    };
  };

  const pointerPairGeometry = (pointers) => {
    if (!Array.isArray(pointers) || pointers.length < 2) return null;
    const [first, second] = pointers;
    if (![first?.x, first?.y, second?.x, second?.y].every(Number.isFinite)) return null;
    return {
      distance: Math.hypot(second.x - first.x, second.y - first.y),
      midpoint: {
        x: (first.x + second.x) / 2,
        y: (first.y + second.y) / 2,
      },
    };
  };

  const pinchTransformFromStart = ({
    startScale,
    startPanX,
    startPanY,
    startDistance,
    currentDistance,
    startMidpoint,
    currentMidpoint,
    center,
    minimumScale = 1,
    maximumScale = 5,
  }) => {
    const safeStartScale = clampScale(startScale, minimumScale, maximumScale);
    const safeStartDistance = Math.max(Number(startDistance) || 0, 0.0001);
    const scale = clampScale(
      safeStartScale * (Math.max(Number(currentDistance) || 0, 0) / safeStartDistance),
      minimumScale,
      maximumScale,
    );
    const ratio = scale / safeStartScale;
    const centerX = Number(center?.x) || 0;
    const centerY = Number(center?.y) || 0;
    const initialMidpointX = Number(startMidpoint?.x) || 0;
    const initialMidpointY = Number(startMidpoint?.y) || 0;
    const nextMidpointX = Number(currentMidpoint?.x) || 0;
    const nextMidpointY = Number(currentMidpoint?.y) || 0;
    return {
      scale,
      x: nextMidpointX - centerX - ratio * (initialMidpointX - centerX - (Number(startPanX) || 0)),
      y: nextMidpointY - centerY - ratio * (initialMidpointY - centerY - (Number(startPanY) || 0)),
    };
  };

  const clampZoomOffset = (value, viewportSize, scale) => {
    const boundary = Math.max(0, (Math.max(viewportSize, 0) * (Math.max(scale, 1) - 1)) / 2);
    return Math.min(Math.max(value, -boundary), boundary);
  };

  const clampElevatedPan = ({
    panX,
    panY,
    bounds,
    scale,
    viewportWidth,
    viewportHeight,
    viewportLeft = 0,
    viewportTop = 0,
    strictBounds = false,
    minimumVisible = 72,
    fittedPanRatio = 0.18,
    allowFittedTravel = false,
  }) => {
    if (!bounds) return { x: 0, y: 0 };
    const safeScale = Math.max(scale || 1, 1);
    const scaledWidth = Math.max(bounds.width || 0, 1) * safeScale;
    const scaledHeight = Math.max(bounds.height || 0, 1) * safeScale;
    const centerX = (bounds.left || 0) + (bounds.width || 0) / 2;
    const centerY = (bounds.top || 0) + (bounds.height || 0) / 2;
    const safeViewportWidth = Math.max(viewportWidth || 0, 1);
    const safeViewportHeight = Math.max(viewportHeight || 0, 1);
    if (!strictBounds) {
      const visibleX = Math.min(minimumVisible, scaledWidth / 2);
      const visibleY = Math.min(minimumVisible, scaledHeight / 2);
      const minimumCenterX = viewportLeft + visibleX - scaledWidth / 2;
      const maximumCenterX = viewportLeft + safeViewportWidth - visibleX + scaledWidth / 2;
      const minimumCenterY = viewportTop + visibleY - scaledHeight / 2;
      const maximumCenterY = viewportTop + safeViewportHeight - visibleY + scaledHeight / 2;
      return {
        x: Math.min(maximumCenterX - centerX, Math.max(minimumCenterX - centerX, Number(panX) || 0)),
        y: Math.min(maximumCenterY - centerY, Math.max(minimumCenterY - centerY, Number(panY) || 0)),
      };
    }
    const viewportRight = viewportLeft + safeViewportWidth;
    const viewportBottom = viewportTop + safeViewportHeight;
    const centeredX = viewportLeft + safeViewportWidth / 2 - centerX;
    const centeredY = viewportTop + safeViewportHeight / 2 - centerY;
    const fittedTravel = (scaledSize, sourceSize, viewportSize) => {
      if (!allowFittedTravel || safeScale <= 1.000001) return 0;
      return Math.max(
        0,
        Math.min(
          scaledSize * fittedPanRatio,
          viewportSize * 0.12,
          (sourceSize * (safeScale - 1)) / 2,
        ),
      );
    };
    const fittedTravelX = fittedTravel(scaledWidth, Math.max(bounds.width || 0, 1), safeViewportWidth);
    const fittedTravelY = fittedTravel(scaledHeight, Math.max(bounds.height || 0, 1), safeViewportHeight);
    const minimumX =
      scaledWidth <= safeViewportWidth ? centeredX - fittedTravelX : viewportRight - centerX - scaledWidth / 2;
    const maximumX =
      scaledWidth <= safeViewportWidth ? centeredX + fittedTravelX : viewportLeft - centerX + scaledWidth / 2;
    const minimumY =
      scaledHeight <= safeViewportHeight ? centeredY - fittedTravelY : viewportBottom - centerY - scaledHeight / 2;
    const maximumY =
      scaledHeight <= safeViewportHeight ? centeredY + fittedTravelY : viewportTop - centerY + scaledHeight / 2;

    return {
      x: Math.min(maximumX, Math.max(minimumX, Number(panX) || 0)),
      y: Math.min(maximumY, Math.max(minimumY, Number(panY) || 0)),
    };
  };

  const centerBoundsInViewport = ({ bounds, viewportWidth, viewportHeight, viewportLeft = 0, viewportTop = 0 }) => {
    if (!bounds) return null;
    return {
      left: viewportLeft + (Math.max(viewportWidth || 0, 0) - bounds.width) / 2,
      top: viewportTop + (Math.max(viewportHeight || 0, 0) - bounds.height) / 2,
      width: bounds.width,
      height: bounds.height,
    };
  };

  const shopifyImageUrl = (source, width, baseUrl = 'https://shopify.invalid') => {
    if (!source) return '';
    try {
      const url = new URL(source, baseUrl);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return source;
    }
  };

  const familyCardCacheLimit = 4;
  const familyCardCacheLifetime = 60_000;

  class AutoplayState {
    constructor(timerApi = {}) {
      this.setTimer = timerApi.setTimer || global.setTimeout.bind(global);
      this.clearTimer = timerApi.clearTimer || global.clearTimeout.bind(global);
      this.timerId = null;
      this.userInteracted = false;
    }

    arm(callback, delay, allowed) {
      this.clear();
      if (this.userInteracted || !allowed) return false;
      this.timerId = this.setTimer(() => {
        this.timerId = null;
        if (!this.userInteracted) callback();
      }, delay);
      return true;
    }

    clear() {
      if (this.timerId === null) return;
      this.clearTimer(this.timerId);
      this.timerId = null;
    }

    stopByUser() {
      this.userInteracted = true;
      this.clear();
    }

    get hasTimer() {
      return this.timerId !== null;
    }
  }

  global.ScoreggiaMediaCore = {
    AutoplayState,
    carouselDistanceThreshold,
    carouselGestureProfile,
    carouselReleaseDuration,
    centerBoundsInViewport,
    clampElevatedPan,
    clampScale,
    clampZoomOffset,
    containedImageBounds,
    desktopZoomReturnGeometry,
    familyProductUrl,
    mediaHandoffUrl,
    gestureIntent,
    loopNavigationPlan,
    mappedVariantForMedia,
    normalizeWheelDelta,
    physicalIndexForLogical,
    productMediaContext,
    productLinkForSlide,
    productUrlWithVariant,
    pinchTransformFromStart,
    pointerPairGeometry,
    recentPointerVelocity,
    recentPointerTrajectory,
    recentVelocityMaximumAge,
    recentTrajectoryWindow,
    recentVelocityWindow,
    recordRecentPointerSample,
    shopifyImageUrl,
    swipeDirection,
    responsiveSwipeDirection,
    responsiveTouchDistanceThreshold,
    responsiveTouchGestureProfile,
    anticipatoryDirection,
    anticipatoryGestureIntent,
    anticipatoryPointerTrajectory,
    anticipatoryPointerVelocity,
    anticipatoryReleaseDecision,
    anticipatoryReleaseDuration,
    anticipatorySampleLimit,
    anticipatoryTouchDistanceThreshold,
    anticipatoryTouchGestureProfile,
    anticipatoryTrajectoryWindow,
    anticipatoryVelocityMaximumAge,
    anticipatoryVelocityWindow,
    anticipatoryVisualLead,
    anticipatoryVisualLeadLimit,
    pointerEventSampleBatch,
    predictedSampleLimit,
    recentVerticalAcceleration,
    recordAnticipatoryPointerSample,
    wheelScaleFromDelta,
    wrapIndex,
    zoomTransformAroundPoint,
    zoomGestureOutcome,
    familyCardCacheLimit,
    familyCardCacheLifetime,
  };

  if (!global.HTMLElement || !global.customElements) return;

  class CircularCarousel {
    constructor(root, options = {}) {
      this.root = root;
      this.options = options;
      this.track = root.querySelector(options.trackSelector || '[data-scoreggia-media-track]');
      this.viewport = root.querySelector(options.viewportSelector || '[data-scoreggia-media-viewport]');
      this.slides = Array.from(root.querySelectorAll(options.slideSelector || '[data-scoreggia-media-slide]'));
      this.thumbnails = Array.from(root.querySelectorAll(options.thumbnailSelector || '[data-scoreggia-media-thumbnail]'));
      this.previousButton = root.querySelector(options.previousSelector || '[data-scoreggia-media-previous]');
      this.nextButton = root.querySelector(options.nextSelector || '[data-scoreggia-media-next]');
      this.currentOutput = root.querySelector(options.currentSelector || '[data-scoreggia-media-current]');
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.pointer = null;
      this.suppressNextClick = false;
      this.suppressClickTimer = null;
      this.ignoreMouseUntil = 0;
      this.activePointerListeners = null;
      this.abortController = null;
      this.loopClones = [];
      this.loopCorrection = null;
      this.loopCorrectionTimer = null;
      this.transitionRestoreFrame = null;
      this.releaseDurationTimer = null;
      this.gestureRenderFrame = null;
      this.gestureLeadTimer = null;
      this.pendingNavigations = [];
      this.currentPhysicalIndex = physicalIndexForLogical(this.activeIndex, this.slides.length);
    }

    connect() {
      if (this.abortController || !this.track || !this.viewport || !this.slides.length) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.createLoopClones();

      this.previousButton?.removeAttribute('disabled');
      this.nextButton?.removeAttribute('disabled');
      this.previousButton?.addEventListener('click', () => this.move(-1, { user: true, source: 'previous' }), {
        signal,
      });
      this.nextButton?.addEventListener('click', () => this.move(1, { user: true, source: 'next' }), { signal });

      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.goTo(index, { user: true, source: 'thumbnail' });
          },
          { signal },
        );
      });

      this.root.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      this.track.addEventListener('transitionend', (event) => this.handleTrackTransitionEnd(event), { signal });
      if ('PointerEvent' in global) {
        this.viewport.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
        this.viewport.addEventListener('lostpointercapture', (event) => this.handleLostPointerCapture(event), {
          signal,
        });
      } else {
        this.viewport.addEventListener('touchstart', (event) => this.handleTouchStart(event), {
          passive: true,
          signal,
        });
        this.viewport.addEventListener('touchmove', (event) => this.handleTouchMove(event), {
          passive: false,
          signal,
        });
        this.viewport.addEventListener('touchend', (event) => this.handleTouchEnd(event), { signal });
        this.viewport.addEventListener('touchcancel', () => this.cancelPointer(), { signal });
        this.viewport.addEventListener('mousedown', (event) => this.handleMouseDown(event), { signal });
        this.viewport.addEventListener('mousemove', (event) => this.handleMouseMove(event), {
          passive: false,
          signal,
        });
        this.viewport.addEventListener('mouseup', (event) => this.handleMouseEnd(event), { signal });
        this.viewport.addEventListener('mouseleave', () => this.cancelPointer(), { signal });
      }
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.viewport.addEventListener('click', (event) => this.handleViewportClick(event), { capture: true, signal });

      this.goTo(this.activeIndex, { immediate: true, source: 'initialize' });
    }

    disconnect() {
      this.abortController?.abort();
      this.abortController = null;
      this.cancelPointer();
      this.clearClickSuppression();
      this.clearLoopState();
      this.clearReleaseDuration();
      this.cleanupLoopClones();
    }

    refresh() {
      const activeMediaId = this.slides[this.activeIndex]?.dataset.mediaId;
      this.prepareForMutation();
      this.slides = Array.from(this.root.querySelectorAll(this.options.slideSelector || '[data-scoreggia-media-slide]'));
      this.thumbnails = Array.from(
        this.root.querySelectorAll(this.options.thumbnailSelector || '[data-scoreggia-media-thumbnail]'),
      );
      const refreshedIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === activeMediaId);
      this.createLoopClones();
      this.goTo(refreshedIndex >= 0 ? refreshedIndex : 0, { immediate: true, source: 'refresh' });
    }

    prepareForMutation() {
      if (!this.track) return;
      this.cancelPointer(true, true, true);
      this.clearLoopState();
      this.cleanupLoopClones();
      this.setImmediatePosition(Math.max(0, this.activeIndex));
    }

    createLoopClones() {
      this.cleanupLoopClones();
      if (!this.track || this.slides.length <= 1) {
        this.currentPhysicalIndex = Math.max(0, this.activeIndex);
        return;
      }

      const lastClone = this.createLoopClone(this.slides[this.slides.length - 1], 'last');
      const firstClone = this.createLoopClone(this.slides[0], 'first');
      this.track.insertBefore(lastClone, this.track.firstChild);
      this.track.append(firstClone);
      this.loopClones = [lastClone, firstClone];
      this.currentPhysicalIndex = physicalIndexForLogical(this.activeIndex, this.slides.length);
    }

    createLoopClone(slide, cloneType) {
      const clone = slide.cloneNode(true);
      const nodes = [clone, ...clone.querySelectorAll('*')];
      nodes.forEach((node) => {
        Array.from(node.attributes || []).forEach((attribute) => {
          if (attribute.name === 'id' || attribute.name.startsWith('data-') || attribute.name.startsWith('aria-')) {
            node.removeAttribute(attribute.name);
          }
        });
        node.classList?.remove('is-active', 'is-dragging', 'is-zoomed', 'is-scoreggia-elevated-zoom-source');
        if (node.matches?.('a')) node.removeAttribute('href');
        if (node.matches?.('button, input, select, textarea')) node.disabled = true;
        if (node.matches?.('[contenteditable]')) node.setAttribute('contenteditable', 'false');
        if (
          node.matches?.(
            'a, button, input, select, textarea, iframe, summary, audio[controls], video[controls], [contenteditable], [tabindex]',
          )
        ) {
          node.setAttribute('tabindex', '-1');
        }
      });
      clone.dataset.scoreggiaLoopClone = cloneType;
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.setAttribute('role', 'presentation');
      return clone;
    }

    cleanupLoopClones() {
      this.track?.querySelectorAll('[data-scoreggia-loop-clone]').forEach((clone) => clone.remove());
      this.loopClones = [];
    }

    move(direction, options = {}) {
      this.goTo(this.activeIndex + direction, { ...options, direction });
    }

    goTo(index, options = {}) {
      if (!this.slides.length) return;
      if (this.loopCorrection && !options.replayQueuedNavigation) {
        const lastQueuedIndex = this.pendingNavigations[this.pendingNavigations.length - 1]?.index;
        const queueBaseIndex = Number.isInteger(lastQueuedIndex) ? lastQueuedIndex : this.activeIndex;
        const queuedIndex = options.direction
          ? wrapIndex(queueBaseIndex + options.direction, this.slides.length)
          : wrapIndex(index, this.slides.length);
        this.pendingNavigations.push({ index: queuedIndex, options });
        if (this.pendingNavigations.length > 12) this.pendingNavigations.shift();
        return;
      }
      const nextIndex = wrapIndex(index, this.slides.length);
      const previousIndex = this.activeIndex;
      const animate = !options.immediate && !this.reducedMotion;
      const navigationPlan = loopNavigationPlan({
        previousIndex,
        nextIndex,
        direction: options.direction || 0,
        count: this.slides.length,
        animate,
      });
      if (options.user) this.signalInteraction(options.source || 'navigation');
      this.options.beforeChange?.(nextIndex, previousIndex, options);
      this.activeIndex = nextIndex;
      if (animate) this.prepareAnimatedPosition(options.releaseDuration);
      else this.setImmediatePosition(navigationPlan.physicalIndex);
      if (animate) this.setTrackPosition(navigationPlan.physicalIndex);
      this.track.style.setProperty('--scoreggia-media-index', String(nextIndex));

      if (navigationPlan.correctionPhysicalIndex !== null) {
        this.startLoopCorrection(navigationPlan.correctionPhysicalIndex);
      }

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if ('inert' in slide) slide.inert = !active;
      });
      this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
        if (thumbnailIndex === nextIndex) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      if (this.currentOutput) this.currentOutput.textContent = String(nextIndex + 1);

      const activeThumbnail = this.thumbnails[nextIndex];
      if (activeThumbnail && options.scrollThumbnail !== false) {
        this.scrollThumbnailHorizontally(activeThumbnail, options);
      }
      this.options.afterChange?.(nextIndex, previousIndex, options);
    }

    setTrackPosition(physicalIndex) {
      this.currentPhysicalIndex = physicalIndex;
      this.track.style.transform = `translate3d(${-100 * physicalIndex}%, 0, 0)`;
    }

    setImmediatePosition(physicalIndex, afterRestore) {
      if (!this.track) return;
      this.clearReleaseDuration();
      if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
      this.transitionRestoreFrame = null;
      this.track.classList.remove('is-dragging');
      this.track.classList.add('is-immediate');
      this.setTrackPosition(physicalIndex);
      this.track.getBoundingClientRect();
      this.transitionRestoreFrame = global.requestAnimationFrame(() => {
        this.transitionRestoreFrame = null;
        this.track?.classList.remove('is-immediate');
        afterRestore?.();
      });
    }

    prepareAnimatedPosition(releaseDuration) {
      if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
      this.transitionRestoreFrame = null;
      this.applyReleaseDuration(releaseDuration);
      this.track.classList.remove('is-immediate', 'is-dragging');
      this.track.getBoundingClientRect();
    }

    startLoopCorrection(correctionPhysicalIndex) {
      this.clearLoopCorrection();
      this.loopCorrection = { correctionPhysicalIndex };
      this.loopCorrectionTimer = global.setTimeout(() => this.finishLoopCorrection(), 500);
    }

    handleTrackTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') return;
      this.clearReleaseDuration();
      if (this.loopCorrection) this.finishLoopCorrection();
    }

    finishLoopCorrection() {
      const correction = this.loopCorrection;
      if (!correction) return;
      this.clearLoopCorrection();
      this.setImmediatePosition(correction.correctionPhysicalIndex, () => this.replayNextNavigation());
    }

    replayNextNavigation() {
      const queuedNavigation = this.pendingNavigations[this.pendingNavigations.length - 1];
      this.pendingNavigations = [];
      if (!queuedNavigation) return;
      this.goTo(queuedNavigation.index, { ...queuedNavigation.options, replayQueuedNavigation: true });
    }

    clearLoopCorrection() {
      if (this.loopCorrectionTimer !== null) global.clearTimeout(this.loopCorrectionTimer);
      this.loopCorrectionTimer = null;
      this.loopCorrection = null;
    }

    clearLoopState() {
      this.clearLoopCorrection();
      this.pendingNavigations = [];
      if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
      this.transitionRestoreFrame = null;
      this.clearReleaseDuration();
    }

    scrollThumbnailHorizontally(thumbnail, options = {}) {
      const rail = thumbnail.closest('.scoreggia-card-media__thumbnail-rail, .scoreggia-product-media__thumbnail-list');
      const item = thumbnail.closest('.scoreggia-product-media__thumbnail-item') || thumbnail;
      if (!rail || !item || typeof rail.scrollTo !== 'function') return;
      const railBounds = rail.getBoundingClientRect();
      const itemBounds = item.getBoundingClientRect();
      let left = rail.scrollLeft;
      if (itemBounds.left < railBounds.left) left -= railBounds.left - itemBounds.left;
      else if (itemBounds.right > railBounds.right) left += itemBounds.right - railBounds.right;
      if (left === rail.scrollLeft) return;
      rail.scrollTo({
        left,
        behavior: this.reducedMotion || options.immediate ? 'auto' : 'smooth',
      });
    }

    signalInteraction(source) {
      this.options.onInteraction?.(source);
    }

    handleViewportClick(event) {
      if (!this.suppressNextClick) return;
      this.clearClickSuppression();
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    handleKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.closest('input, select, textarea, video, model-viewer, iframe, [contenteditable="true"]')) {
        return;
      }

      const thumbnailIndex = this.thumbnails.indexOf(event.target);
      if ((event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') && thumbnailIndex >= 0) {
        event.preventDefault();
        this.goTo(thumbnailIndex, { user: true, source: 'thumbnail' });
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1, { user: true, source: 'keyboard' });
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1, { user: true, source: 'keyboard' });
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0, { user: true, immediate: true, source: 'keyboard' });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1, { user: true, immediate: true, source: 'keyboard' });
      } else if (event.key === 'Escape' && this.options.onEscape?.()) {
        event.preventDefault();
        this.signalInteraction('escape');
      }
    }

    handlePointerDown(event) {
      if (event.isPrimary === false) {
        if (event.pointerType === 'touch' && this.pointer?.pointerType === 'touch') this.cancelPointer();
        return;
      }
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const started = this.startPointer(
        event.pointerId,
        event.clientX,
        event.clientY,
        event.target,
        event.pointerType || 'pointer',
      );
      if (!started) return;
      this.bindActivePointerListeners();
      if (event.pointerType === 'touch') this.signalInteraction('touch');
    }

    handlePointerMove(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const currentTime = global.performance.now();
      const latestTime = this.pointer.samples[this.pointer.samples.length - 1]?.time;
      const batch = pointerEventSampleBatch(event, currentTime, latestTime);
      batch.realSamples.forEach((sample) => {
        if (!this.pointer) return;
        this.pointer.samples = recordAnticipatoryPointerSample(
          this.pointer.samples,
          sample.x,
          sample.time,
          sample.y,
        );
      });
      if (!this.pointer) return;
      this.updatePointer(batch.actual.x, batch.actual.y, event, true, batch.actual.time, {
        predictedSamples: batch.predictedSamples,
        predictedSupported: batch.predictedSupported,
        recordSample: false,
      });
    }

    handlePointerEnd(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      this.finishPointer(event.clientX, event.clientY, event.timeStamp);
    }

    handlePointerCancel(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      this.cancelPointer(true, true, true);
    }

    handleLostPointerCapture(event) {
      if (this.pointer?.id !== event.pointerId) return;
      this.pointer.captured = false;
    }

    bindActivePointerListeners() {
      this.clearActivePointerListeners();
      const move = (event) => this.handlePointerMove(event);
      const end = (event) => this.handlePointerEnd(event);
      const cancel = (event) => this.handlePointerCancel(event);
      global.addEventListener('pointermove', move, { passive: false });
      global.addEventListener('pointerup', end);
      global.addEventListener('pointercancel', cancel);
      this.activePointerListeners = { move, end, cancel };
    }

    clearActivePointerListeners() {
      const listeners = this.activePointerListeners;
      if (!listeners) return;
      global.removeEventListener('pointermove', listeners.move);
      global.removeEventListener('pointerup', listeners.end);
      global.removeEventListener('pointercancel', listeners.cancel);
      this.activePointerListeners = null;
    }

    handleTouchStart(event) {
      if (event.touches.length !== 1) {
        this.cancelPointer();
        return;
      }
      const touch = event.changedTouches[0];
      this.ignoreMouseUntil = global.performance.now() + 800;
      const started = this.startPointer(`touch-${touch.identifier}`, touch.clientX, touch.clientY, event.target, 'touch');
      if (started) this.signalInteraction('touch');
    }

    handleTouchMove(event) {
      if (!this.pointer?.id?.startsWith('touch-')) return;
      const identifier = Number.parseInt(this.pointer.id.slice(6), 10);
      const touch = Array.from(event.touches).find((item) => item.identifier === identifier);
      if (touch) this.updatePointer(touch.clientX, touch.clientY, event, false);
    }

    handleTouchEnd(event) {
      if (!this.pointer?.id?.startsWith('touch-')) return;
      const identifier = Number.parseInt(this.pointer.id.slice(6), 10);
      const touch = Array.from(event.changedTouches).find((item) => item.identifier === identifier);
      if (touch) this.finishPointer(touch.clientX, touch.clientY);
      else this.cancelPointer();
    }

    handleMouseDown(event) {
      if (event.button !== 0 || global.performance.now() < this.ignoreMouseUntil) return;
      this.startPointer('mouse', event.clientX, event.clientY, event.target, 'mouse');
    }

    handleMouseMove(event) {
      if (this.pointer?.id !== 'mouse') return;
      this.updatePointer(event.clientX, event.clientY, event, false);
    }

    handleMouseEnd(event) {
      if (this.pointer?.id !== 'mouse') return;
      this.finishPointer(event.clientX, event.clientY);
    }

    startPointer(id, clientX, clientY, target, pointerType) {
      if (this.options.isZoomed?.()) {
        this.signalInteraction('zoom');
        return false;
      }
      if (target.closest?.('[data-scoreggia-media-control]')) return false;
      if (target.closest?.('video, model-viewer, iframe')) {
        this.signalInteraction('native-media');
        return false;
      }
      if (this.slides.length <= 1) return false;
      this.cancelPointer(true, true, true);
      const startedAt = global.performance.now();
      this.pointer = {
        id,
        pointerType,
        captureTarget: target?.setPointerCapture ? target : this.viewport,
        startX: clientX,
        startY: clientY,
        lastX: clientX,
        lastY: clientY,
        startedAt,
        actualX: clientX,
        actualY: clientY,
        actualTime: startedAt,
        viewportWidth: Math.max(this.viewport.clientWidth || 0, 1),
        intent: null,
        probableHorizontal: false,
        meaningfulSamples: 0,
        direction: 0,
        visualLead: 0,
        visualOffset: 0,
        moved: false,
        captured: false,
        samples: recordRecentPointerSample([], clientX, startedAt, clientY),
      };
      return true;
    }

    updatePointer(clientX, clientY, event, canCapture, eventTimeStamp = null, options = {}) {
      const pointer = this.pointer;
      if (!pointer || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
      const deltaX = clientX - pointer.startX;
      const deltaY = clientY - pointer.startY;
      const currentTime = global.performance.now();
      const sampleTime = normalizePointerTimestamp(eventTimeStamp, currentTime);
      if (options.recordSample !== false) {
        pointer.samples = recordAnticipatoryPointerSample(pointer.samples, clientX, sampleTime, clientY);
      }
      pointer.actualX = clientX;
      pointer.actualY = clientY;
      pointer.actualTime = sampleTime;

      if (!pointer.intent) {
        if (pointer.pointerType === 'mouse') {
          const profile = carouselGestureProfile('mouse');
          if (Math.hypot(deltaX, deltaY) > profile.deadZone) pointer.meaningfulSamples += 1;
          const trajectory = recentPointerTrajectory({ samples: pointer.samples, now: sampleTime });
          const intent = gestureIntent({
            deltaX,
            deltaY,
            threshold: profile.deadZone,
            ratio: profile.horizontalRatio,
            verticalRatio: profile.verticalRatio,
            maximumUndecidedDistance: profile.maximumUndecidedDistance,
            undecidedSamples: pointer.meaningfulSamples,
            maximumUndecidedSamples: profile.maximumUndecidedSamples,
            recentDeltaX: trajectory.x,
            recentDeltaY: trajectory.y,
          });
          if (intent === 1) pointer.intent = 'horizontal';
          else if (intent === -1) {
            pointer.intent = 'vertical';
            this.cancelPointer(true, true, true);
            return;
          }
        } else {
          if (Math.hypot(deltaX, deltaY) > anticipatoryTouchGestureProfile.deadZone) {
            pointer.meaningfulSamples += 1;
          }
          const trajectory = anticipatoryPointerTrajectory({ samples: pointer.samples, now: sampleTime });
          const intent = anticipatoryGestureIntent({
            deltaX,
            deltaY,
            recentDeltaX: trajectory.x,
            recentDeltaY: trajectory.y,
            meaningfulSamples: pointer.meaningfulSamples,
            verticalAcceleration: recentVerticalAcceleration(pointer.samples),
          });
          if (intent === 'horizontal') {
            pointer.intent = 'horizontal';
            pointer.probableHorizontal = false;
          } else if (intent === 'vertical') {
            pointer.intent = 'vertical';
            this.cancelPointer(true, true, true);
            return;
          } else {
            pointer.probableHorizontal = intent === 'probable-horizontal';
          }
        }
      }

      const trajectory =
        pointer.pointerType === 'mouse'
          ? recentPointerTrajectory({ samples: pointer.samples, now: sampleTime })
          : anticipatoryPointerTrajectory({ samples: pointer.samples, now: sampleTime });
      const nextDirection = anticipatoryDirection({
        currentDirection: pointer.direction,
        deltaX,
        recentDeltaX: trajectory.x,
      });
      if (pointer.direction && nextDirection && pointer.direction !== nextDirection) pointer.visualLead = 0;
      pointer.direction = nextDirection;

      if (pointer.intent === 'horizontal') {
        pointer.moved = true;
        if (canCapture) this.capturePointer(event.pointerId);
        if (event.cancelable) event.preventDefault();
        pointer.visualLead =
          pointer.pointerType === 'mouse'
            ? 0
            : anticipatoryVisualLead({
                samples: pointer.samples,
                predictedSamples: options.predictedSamples,
                predictedSupported: options.predictedSupported,
                actualX: clientX,
                actualTime: sampleTime,
                now: currentTime,
                width: pointer.viewportWidth,
                direction: pointer.direction,
              });
        pointer.visualOffset = deltaX + pointer.visualLead;
        this.scheduleGestureLeadCleanup();
        this.scheduleDragFeedback();
      } else if (pointer.probableHorizontal) {
        const previewLead = anticipatoryVisualLead({
          samples: pointer.samples,
          predictedSamples: options.predictedSamples,
          predictedSupported: options.predictedSupported,
          actualX: clientX,
          actualTime: sampleTime,
          now: currentTime,
          width: pointer.viewportWidth,
          direction: pointer.direction,
        });
        pointer.visualLead = previewLead;
        pointer.visualOffset = Math.max(
          -anticipatoryTouchGestureProfile.previewClamp,
          Math.min(
            anticipatoryTouchGestureProfile.previewClamp,
            deltaX * anticipatoryTouchGestureProfile.previewDamping +
              previewLead * anticipatoryTouchGestureProfile.previewLeadDamping,
          ),
        );
        this.scheduleGestureLeadCleanup();
        this.scheduleDragFeedback();
      } else if (this.track?.classList.contains('is-dragging')) {
        pointer.visualLead = 0;
        pointer.visualOffset = 0;
        this.clearGestureLeadTimer();
        this.scheduleDragFeedback();
      }

      pointer.lastX = clientX;
      pointer.lastY = clientY;
    }

    finishPointer(clientX, clientY, eventTimeStamp = null) {
      const pointer = this.pointer;
      if (!pointer) return;
      const deltaX = clientX - pointer.startX;
      const deltaY = clientY - pointer.startY;
      const finishedAt = global.performance.now();
      const finalTime = normalizePointerTimestamp(eventTimeStamp, finishedAt);
      if (clientX !== pointer.actualX || clientY !== pointer.actualY) {
        pointer.samples = recordAnticipatoryPointerSample(pointer.samples, clientX, finalTime, clientY);
      }
      pointer.actualX = clientX;
      pointer.actualY = clientY;
      pointer.actualTime = finalTime;
      const velocityX =
        pointer.pointerType === 'mouse'
          ? recentPointerVelocity({ samples: pointer.samples, now: finishedAt })
          : anticipatoryPointerVelocity({ samples: pointer.samples, now: finishedAt });
      const recognizedDrag = pointer.intent === 'horizontal';
      let direction = 0;
      let releaseDuration;
      if (recognizedDrag && pointer.pointerType !== 'mouse') {
        const decision = anticipatoryReleaseDecision({
          deltaX,
          width: pointer.viewportWidth,
          velocityX,
          samples: pointer.samples,
          now: finishedAt,
          horizontalIntent: true,
        });
        direction = decision.direction;
        releaseDuration = anticipatoryReleaseDuration({
          width: pointer.viewportWidth,
          deltaX,
          velocityX,
          accepted: decision.accepted,
          distanceThreshold: decision.distanceThreshold,
        });
      } else {
        direction = recognizedDrag
          ? responsiveSwipeDirection({
              deltaX,
              deltaY,
              duration: finishedAt - pointer.startedAt,
              width: pointer.viewportWidth,
              pointerType: pointer.pointerType,
              velocityX,
              horizontalIntent: true,
            })
          : 0;
        releaseDuration = carouselReleaseDuration({
          width: pointer.viewportWidth,
          deltaX,
          velocityX,
          accepted: Boolean(direction),
        });
      }

      if (recognizedDrag) this.markClickForSuppression();
      if (recognizedDrag) {
        this.clearGestureRenderFrame();
        pointer.visualLead = 0;
        pointer.visualOffset = deltaX;
        this.setDragFeedback(deltaX);
      }
      this.cancelPointer(true, !recognizedDrag, !recognizedDrag);
      if (direction) {
        this.move(direction, { user: true, source: 'swipe', releaseDuration });
      } else if (recognizedDrag) {
        this.restoreTrackAfterDrag({ releaseDuration });
        this.signalInteraction('drag');
      }
    }

    capturePointer(pointerId) {
      const pointer = this.pointer;
      const captureTarget = pointer?.captureTarget;
      if (!pointer || pointer.captured || !captureTarget?.setPointerCapture || captureTarget.isConnected === false) return;
      try {
        captureTarget.setPointerCapture(pointerId);
        pointer.captured = captureTarget.hasPointerCapture ? captureTarget.hasPointerCapture(pointerId) : true;
      } catch {
        pointer.captured = false;
      }
    }

    setDragFeedback(deltaX) {
      if (!this.track || this.loopCorrection) return;
      const basePhysicalIndex = physicalIndexForLogical(this.activeIndex, this.slides.length);
      const viewportWidth = Math.max(this.pointer?.viewportWidth || this.viewport.clientWidth || 0, 1);
      const offset = Math.max(-viewportWidth, Math.min(viewportWidth, deltaX));
      if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
      this.transitionRestoreFrame = null;
      this.track.classList.remove('is-immediate');
      this.track.classList.add('is-dragging');
      this.track.style.transform = `translate3d(calc(${-100 * basePhysicalIndex}% + ${offset}px), 0, 0)`;
    }

    scheduleDragFeedback() {
      if (this.track && !this.loopCorrection) {
        if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
        this.transitionRestoreFrame = null;
        this.track.classList.remove('is-immediate');
        this.track.classList.add('is-dragging');
      }
      if (this.gestureRenderFrame !== null) return;
      this.gestureRenderFrame = global.requestAnimationFrame(() => {
        this.gestureRenderFrame = null;
        if (!this.pointer) return;
        this.setDragFeedback(this.pointer.visualOffset);
      });
    }

    clearGestureRenderFrame() {
      if (this.gestureRenderFrame !== null) global.cancelAnimationFrame(this.gestureRenderFrame);
      this.gestureRenderFrame = null;
    }

    scheduleGestureLeadCleanup() {
      this.clearGestureLeadTimer();
      const pointer = this.pointer;
      if (!pointer?.visualLead) return;
      const expectedActualTime = pointer.actualTime;
      this.gestureLeadTimer = global.setTimeout(() => {
        this.gestureLeadTimer = null;
        const activePointer = this.pointer;
        if (!activePointer || activePointer.actualTime !== expectedActualTime) return;
        activePointer.visualLead = 0;
        const deltaX = activePointer.actualX - activePointer.startX;
        activePointer.visualOffset = activePointer.probableHorizontal
          ? Math.max(
              -anticipatoryTouchGestureProfile.previewClamp,
              Math.min(
                anticipatoryTouchGestureProfile.previewClamp,
                deltaX * anticipatoryTouchGestureProfile.previewDamping,
              ),
            )
          : deltaX;
        this.scheduleDragFeedback();
      }, anticipatoryTouchGestureProfile.predictionStaleAge + 1);
    }

    clearGestureLeadTimer() {
      if (this.gestureLeadTimer !== null) global.clearTimeout(this.gestureLeadTimer);
      this.gestureLeadTimer = null;
    }

    restoreTrackAfterDrag({ immediate = false, releaseDuration } = {}) {
      if (!this.track?.classList.contains('is-dragging')) return;
      this.track.classList.remove('is-dragging');
      this.track.getBoundingClientRect();
      const physicalIndex = physicalIndexForLogical(this.activeIndex, this.slides.length);
      if (immediate) this.setImmediatePosition(physicalIndex);
      else {
        this.applyReleaseDuration(releaseDuration);
        this.setTrackPosition(physicalIndex);
      }
    }

    applyReleaseDuration(duration) {
      this.clearReleaseDuration();
      if (!Number.isFinite(duration) || !this.track) return;
      this.track.style.setProperty('--scoreggia-media-release-duration', `${duration}ms`);
      this.releaseDurationTimer = global.setTimeout(() => this.clearReleaseDuration(), duration + 80);
    }

    clearReleaseDuration() {
      if (this.releaseDurationTimer !== null) global.clearTimeout(this.releaseDurationTimer);
      this.releaseDurationTimer = null;
      this.track?.style?.removeProperty?.('--scoreggia-media-release-duration');
    }

    markClickForSuppression() {
      this.clearClickSuppression();
      this.suppressNextClick = true;
      this.suppressClickTimer = global.setTimeout(() => this.clearClickSuppression(), 500);
    }

    clearClickSuppression() {
      if (this.suppressClickTimer !== null) global.clearTimeout(this.suppressClickTimer);
      this.suppressClickTimer = null;
      this.suppressNextClick = false;
    }

    cancelPointer(releaseCapture = true, restoreDrag = true, immediateRestore = false) {
      const pointer = this.pointer;
      this.clearGestureRenderFrame();
      this.clearGestureLeadTimer();
      if (pointer) {
        pointer.visualLead = 0;
        pointer.visualOffset = 0;
      }
      this.pointer = null;
      this.clearActivePointerListeners();
      if (restoreDrag) this.restoreTrackAfterDrag({ immediate: immediateRestore });
      const captureTarget = pointer?.captureTarget;
      if (!releaseCapture || !captureTarget?.releasePointerCapture || captureTarget.isConnected === false) return;
      let hasCapture = pointer.captured;
      if (captureTarget.hasPointerCapture) {
        try {
          hasCapture = captureTarget.hasPointerCapture(pointer.id);
        } catch {
          hasCapture = false;
        }
      }
      if (!hasCapture) return;
      try {
        captureTarget.releasePointerCapture(pointer.id);
      } catch {
        return;
      }
    }

    get reducedMotion() {
      return global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  global.ScoreggiaMediaCore.CircularCarousel = CircularCarousel;

  class ScoreggiaCardMedia extends global.HTMLElement {
    connectedCallback() {
      if (this.lifecycleController) return;
      this.lifecycleController = new AbortController();
      if (this.hasAttribute('data-scoreggia-card-family')) {
        this.familyRequestController = null;
        this.familyRequestSequence = 0;
        this.familyContextCache = new Map();
        this.addEventListener('click', (event) => this.handleCardClick(event), {
          signal: this.lifecycleController.signal,
        });
      }
      this.connectCarousel();
    }

    connectCarousel() {
      this.productLink = this.querySelector('[data-card-media-product-link]');
      this.controller = new CircularCarousel(this, {
        trackSelector: '[data-card-media-track]',
        viewportSelector: '[data-card-media-viewport]',
        slideSelector: '[data-card-media-slide]',
        thumbnailSelector: '[data-card-media-thumbnail]',
        previousSelector: '[data-card-media-previous]',
        nextSelector: '[data-card-media-next]',
        currentSelector: '[data-card-media-current]',
        afterChange: (nextIndex) => this.updateProductLink(nextIndex),
      });
      this.controller.connect();
    }

    updateProductLink(index) {
      if (!this.productLink) return;
      const activeSlide = this.controller?.slides[index];
      const nextUrl = productLinkForSlide(this.productLink.dataset.productUrl, activeSlide);
      if (nextUrl) this.productLink.setAttribute('href', nextUrl);
    }

    handleCardClick(event) {
      const productLink = event.target.closest('[data-card-media-product-link]');
      if (productLink && this.hasAttribute('data-style-loading')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const option = event.target.closest('[data-scoreggia-card-style-option]');
      if (
        !option ||
        global.Shopify?.designMode ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        option.hasAttribute('download') ||
        (option.target && option.target !== '_self')
      ) {
        return;
      }

      event.preventDefault();
      const activeOption = this.querySelector('[data-scoreggia-card-style-option][aria-current="true"]');
      if (option === activeOption) {
        if (this.hasAttribute('data-style-loading')) {
          this.familyRequestController?.abort();
          this.familyRequestController = null;
          this.familyRequestSequence += 1;
          this.setFamilyLoading(false);
        }
        option.focus({ preventScroll: true });
        return;
      }
      this.switchCardStyle(option);
    }

    async switchCardStyle(option) {
      this.familyRequestController?.abort();
      this.familyRequestController = new AbortController();
      const { signal } = this.familyRequestController;
      const sequence = ++this.familyRequestSequence;
      this.setFamilyLoading(true, option);

      try {
        const context = await this.fetchFamilyContext(option, signal);
        if (signal.aborted || sequence !== this.familyRequestSequence || !this.isConnected) return;
        this.applyFamilyContext(context, option);
        this.querySelectorAll('[data-scoreggia-card-style-option]').forEach((styleOption) => {
          if (styleOption === option) styleOption.setAttribute('aria-current', 'true');
          else styleOption.removeAttribute('aria-current');
        });
        this.dataset.currentStyleHandle = option.dataset.productHandle || '';
        this.dataset.currentProductId = option.dataset.productId || '';
        this.setFamilyLoading(false);
        const status = this.querySelector('[data-scoreggia-card-family-status]');
        if (status) status.textContent = option.textContent.trim();
        global.requestAnimationFrame(() => option.isConnected && option.focus({ preventScroll: true }));
      } catch (error) {
        if (error?.name === 'AbortError' || sequence !== this.familyRequestSequence) return;
        this.setFamilyLoading(false);
        const status = this.querySelector('[data-scoreggia-card-family-status]');
        if (status) status.textContent = this.dataset.styleSwitchError || '';
      } finally {
        if (sequence === this.familyRequestSequence) this.familyRequestController = null;
      }
    }

    async fetchFamilyContext(option, signal) {
      const cacheKey = option.dataset.productId || option.dataset.productJsonUrl;
      const cached = this.familyContextCache.get(cacheKey);
      if (cached && Date.now() - cached.savedAt <= familyCardCacheLifetime) {
        this.familyContextCache.delete(cacheKey);
        this.familyContextCache.set(cacheKey, cached);
        return cached.context;
      }
      if (cached) this.familyContextCache.delete(cacheKey);

      const response = await fetch(option.dataset.productJsonUrl, {
        cache: 'no-store',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal,
      });
      if (!response.ok) throw new Error('homepage product Style request failed');
      const productData = await response.json();
      const context = productMediaContext({
        productData,
        canonicalUrl: this.dataset.canonicalProductUrl,
        canonicalHandle: this.dataset.canonicalProductHandle,
        currentHandle: option.dataset.productHandle,
        productUrl: option.dataset.productUrl || option.href,
        baseUrl: global.location?.href,
      });
      if (
        String(context.productId) !== String(option.dataset.productId) ||
        !context.productTitle ||
        context.media.length === 0
      ) {
        throw new Error('homepage product Style response was invalid');
      }

      this.familyContextCache.set(cacheKey, { savedAt: Date.now(), context });
      while (this.familyContextCache.size > familyCardCacheLimit) {
        this.familyContextCache.delete(this.familyContextCache.keys().next().value);
      }
      return context;
    }

    setFamilyLoading(loading, option = null) {
      const group = this.querySelector('[data-scoreggia-card-style-group]');
      const productLink = this.querySelector('[data-card-media-product-link]');
      this.querySelectorAll('[data-scoreggia-card-style-option]').forEach((styleOption) => {
        styleOption.toggleAttribute('data-loading', loading && styleOption === option);
      });
      if (loading) {
        this.setAttribute('data-style-loading', '');
        this.setAttribute('aria-busy', 'true');
        group?.setAttribute('aria-busy', 'true');
        productLink?.setAttribute('aria-disabled', 'true');
        const status = this.querySelector('[data-scoreggia-card-family-status]');
        if (status) status.textContent = '';
        return;
      }
      this.removeAttribute('data-style-loading');
      this.removeAttribute('aria-busy');
      group?.removeAttribute('aria-busy');
      productLink?.removeAttribute('aria-disabled');
    }

    applyFamilyContext(context, option) {
      this.controller?.disconnect();
      this.controller = null;
      const carouselId = `${this.dataset.carouselId || 'ScoreggiaCardMedia'}-${context.productId}`;
      const viewport = this.buildFamilyViewport(context, carouselId);
      const previousViewport = this.querySelector('[data-card-media-viewport]');
      if (!previousViewport) throw new Error('homepage product card viewport is missing');
      previousViewport.replaceWith(viewport);

      const previousRail = this.querySelector('.scoreggia-card-media__thumbnail-rail');
      if (this.dataset.showThumbnails === 'true') {
        const rail = this.buildFamilyThumbnailRail(context, carouselId);
        if (previousRail) previousRail.replaceWith(rail);
        else this.append(rail);
      } else {
        previousRail?.remove();
      }

      this.dataset.imageCount = String(context.media.length);
      this.dataset.productTitle = context.productTitle;
      this.setAttribute(
        'aria-label',
        `${this.dataset.familyTitle || context.productTitle} — ${this.dataset.galleryLabel || ''}`.trim(),
      );
      this.connectCarousel();
      this.controller?.goTo(context.activeIndex, { immediate: true, source: 'style' });
      this.productLink?.setAttribute('data-selected-product-id', option.dataset.productId || '');
    }

    buildFamilyViewport(context, carouselId) {
      const viewport = document.createElement('div');
      viewport.className = 'scoreggia-card-media__viewport';
      viewport.dataset.cardMediaViewport = '';

      const track = document.createElement('div');
      track.id = carouselId;
      track.className = 'scoreggia-card-media__track';
      track.dataset.cardMediaTrack = '';
      context.media.forEach((media, index) => track.append(this.buildFamilySlide(media, index, context, carouselId)));
      viewport.append(track);

      const activeMedia = context.media[context.activeIndex] || context.media[0];
      const link = document.createElement('a');
      link.className = 'scoreggia-card-media__product-link';
      link.href = activeMedia?.variantUrl || context.cleanProductUrl;
      link.dataset.cardMediaProductLink = '';
      link.dataset.productUrl = context.cleanProductUrl;
      link.setAttribute('aria-label', this.dataset.familyTitle || context.productTitle);
      link.draggable = false;
      const hiddenLabel = document.createElement('span');
      hiddenLabel.className = 'visually-hidden';
      hiddenLabel.textContent = this.dataset.familyTitle || context.productTitle;
      link.append(hiddenLabel);
      viewport.append(link);

      if (context.media.length > 1) {
        const controls = document.createElement('div');
        controls.className = 'scoreggia-card-media__controls';
        const previous = this.buildFamilyArrow('previous', carouselId, context.productTitle);
        const status = document.createElement('p');
        status.className = 'visually-hidden';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        const current = document.createElement('span');
        current.dataset.cardMediaCurrent = '';
        current.textContent = String(context.activeIndex + 1);
        status.append(current, ` ${this.dataset.ofLabel || 'of'} ${context.media.length}`);
        const next = this.buildFamilyArrow('next', carouselId, context.productTitle);
        controls.append(previous, status, next);
        viewport.append(controls);
      }
      return viewport;
    }

    buildFamilySlide(media, index, context, carouselId) {
      const slide = document.createElement('div');
      slide.id = `${carouselId}-Slide-${index + 1}`;
      slide.className = `scoreggia-card-media__slide${index === context.activeIndex ? ' is-active' : ''}`;
      slide.dataset.cardMediaSlide = '';
      slide.dataset.logicalIndex = String(index);
      slide.dataset.mediaId = String(media.id);
      slide.dataset.mediaPosition = String(media.position);
      slide.dataset.mediaType = media.mediaType;
      if (media.variantId) {
        slide.dataset.variantId = String(media.variantId);
        slide.dataset.variantMediaMatch = media.mappingSource;
        if (media.colorValue) slide.dataset.colorValue = media.colorValue;
      }
      if (media.variantUrl) slide.dataset.variantUrl = media.variantUrl;
      slide.setAttribute('aria-hidden', String(index !== context.activeIndex));

      const image = document.createElement('img');
      image.className = 'scoreggia-card-media__image motion-reduce';
      image.src = shopifyImageUrl(media.preview.src, 1200, global.location?.href);
      image.srcset = [320, 480, 640, 800, 960, 1200]
        .map((width) => `${shopifyImageUrl(media.preview.src, width, global.location?.href)} ${width}w`)
        .join(', ');
      image.sizes = '(min-width: 1280px) min(33vw, 590px), (min-width: 640px) 50vw, 100vw';
      image.alt = media.alt || context.productTitle;
      image.loading = index === context.activeIndex ? 'eager' : 'lazy';
      image.width = media.preview.width;
      image.height = media.preview.height;
      image.draggable = false;
      slide.append(image);
      return slide;
    }

    buildFamilyArrow(direction, carouselId, productTitle) {
      const button = document.createElement('button');
      button.className = `scoreggia-card-media__button scoreggia-card-media__button--${direction}`;
      button.type = 'button';
      button.dataset.scoreggiaMediaControl = '';
      if (direction === 'previous') button.dataset.cardMediaPrevious = '';
      else button.dataset.cardMediaNext = '';
      button.setAttribute('aria-controls', carouselId);
      button.setAttribute(
        'aria-label',
        `${direction === 'previous' ? this.dataset.previousLabel : this.dataset.nextLabel} — ${productTitle}`,
      );
      const icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = direction === 'previous' ? '←' : '→';
      button.append(icon);
      return button;
    }

    buildFamilyThumbnailRail(context, carouselId) {
      const rail = document.createElement('div');
      rail.className = 'scoreggia-card-media__thumbnail-rail';
      rail.setAttribute('aria-label', this.dataset.galleryLabel || '');
      context.media.forEach((media, index) => {
        const thumbnail = document.createElement('a');
        thumbnail.className = 'scoreggia-card-media__thumbnail';
        thumbnail.href = `#${carouselId}-Slide-${index + 1}`;
        thumbnail.dataset.cardMediaThumbnail = '';
        const label = (this.dataset.thumbnailLabelTemplate || 'Load image __INDEX__').replace(
          '__INDEX__',
          String(index + 1),
        );
        thumbnail.setAttribute('aria-label', `${label} — ${context.productTitle}`);
        if (index === context.activeIndex) thumbnail.setAttribute('aria-current', 'true');
        const image = document.createElement('img');
        image.className = 'scoreggia-card-media__thumbnail-image';
        image.src = shopifyImageUrl(media.preview.src, 160, global.location?.href);
        image.srcset = [64, 80, 120, 160]
          .map((width) => `${shopifyImageUrl(media.preview.src, width, global.location?.href)} ${width}w`)
          .join(', ');
        image.sizes = '48px';
        image.loading = 'lazy';
        image.alt = '';
        image.width = media.preview.width;
        image.height = media.preview.height;
        thumbnail.append(image);
        rail.append(thumbnail);
      });
      return rail;
    }

    disconnectedCallback() {
      this.lifecycleController?.abort();
      this.lifecycleController = null;
      this.familyRequestController?.abort();
      this.familyRequestController = null;
      this.familyContextCache?.clear();
      this.controller?.disconnect();
      this.controller = null;
      this.productLink = null;
    }
  }

  if (!global.customElements.get('scoreggia-card-media')) {
    global.customElements.define('scoreggia-card-media', ScoreggiaCardMedia);
  }
})(typeof window === 'undefined' ? globalThis : window);
